import React, { useEffect, useRef, useState, useCallback } from 'react';
import { isEmpty } from 'lodash';
import moment from 'moment';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';

import agentUsersResource from 'src/actions/agentUsers/agentUsersResource';
import { AlertWarning } from '@ad-scripts/presentation/components/common/alert';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { CsvUploadStatus } from 'src/models/Users/CsvUpload';
import EmbedModal, { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import FailedIcon from '@ad-scripts/presentation/components/common/icons/stroke/failed-stroke';
import { FileInput } from 'src/presentation/components/forms/FileInput';
import ExternalLink, { StyledExternalLink } from 'src/presentation/components/pages/shared/ExternalLink';
import SpinnerTwo from '@ad-scripts/presentation/components/common/spinner_two';
import SuccessIcon from '@ad-scripts/presentation/components/common/icons/checkmark_circle';
import { useResourceValues } from 'src/presentation/hooks/useResource';

const MAX_FILE_SIZE = 5 * 1024 * 1024;

function CsvUploadModal({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch();
  const { usersCsvUploadStatus } = useResourceValues(agentUsersResource);
  const [status, setStatus] = useState<CsvUploadStatus>(CsvUploadStatus.NotStarted);

  useEffect(() => {
    if (usersCsvUploadStatus) {
      setStatus(usersCsvUploadStatus);
    }
  }, [usersCsvUploadStatus, setStatus]);

  const handleClose = useCallback(() => {
    dispatch(agentUsersResource.actions.usersCsvUploadReset({}));
    onClose();
  }, [dispatch, onClose]);

  const content = React.useMemo(() => getContentByStatus(status, handleClose), [status, handleClose]);

  return (
    <StyledEmbedModal
      data-aid="users-upload-csv-uploading"
      title={getTitleByStatus(status)}
      disableOutsideClick={true}
      modalSize={ModalSize.DEFAULT}
      content={content}
      onCancel={handleClose}
      showCloseButton={status !== CsvUploadStatus.Started}
    />
  );
}

function getContentByStatus(status: CsvUploadStatus, handleClose: () => void) {
  const elementByStatus: Record<CsvUploadStatus, JSX.Element> = {
    [CsvUploadStatus.NotStarted]: <NotStarted handleClose={handleClose} />,
    [CsvUploadStatus.Started]: <Started />,
    [CsvUploadStatus.Success]: <Success handleClose={handleClose} />,
    [CsvUploadStatus.ServerError]: <ServerError handleClose={handleClose} />,
    [CsvUploadStatus.ExceedsAllowedFilesizeErrror]: <ExceedsAllowedFilesizeErrror handleClose={handleClose} />,
    [CsvUploadStatus.IncorrectFormatError]: <IncorrectFormatError handleClose={handleClose} />,
    [CsvUploadStatus.CompletedWithErrors]: <CompletedWithErrors handleClose={handleClose} />,
  };

  return elementByStatus[status];
}

function getTitleByStatus(status: CsvUploadStatus): string {
  const titleByStatus: Record<CsvUploadStatus, string> = {
    [CsvUploadStatus.NotStarted]: 'Upload CSV',
    [CsvUploadStatus.Started]: 'Upload CSV',
    [CsvUploadStatus.Success]: 'CSV upload complete',
    [CsvUploadStatus.ServerError]: 'CSV upload failed',
    [CsvUploadStatus.ExceedsAllowedFilesizeErrror]: 'CSV file too large',
    [CsvUploadStatus.IncorrectFormatError]: 'CSV file missing columns',
    [CsvUploadStatus.CompletedWithErrors]: 'Incomplete CSV upload',
  };

  return titleByStatus[status];
}

const NotStarted = ({ handleClose }) => {
  return (
    <>
      <Text data-aid="users-upload-csv-first-step-text">
        Add many users at once by uploading a comma-separated values (CSV) file (5MB size limit) based on{' '}
        <ExternalLink
          data-aid="csv-template-link"
          label="this template"
          to="https://help.gladly.com/docs/managing-users-and-roles#create-a-csv-file"
        />
        .
      </Text>

      <AlertWarning>
        <LeftAlignedDiv data-aid="analyst-role-warning">
          <b>Note:</b> Assigning an Analyst role to a user will incur an{' '}
          <StyledExternalLink
            data-aid="additional-cost-help-link"
            label="additional cost"
            to="https://help.gladly.com/implementation/docs/invoice-and-billing-overview#types-of-users"
          />
          .
        </LeftAlignedDiv>
      </AlertWarning>

      <UploadCsvForm handleClose={handleClose} />
    </>
  );
};

function ExceedsAllowedFilesizeErrror({ handleClose }) {
  return (
    <>
      <IconContainer>
        <StyledFailedIcon data-aid="users-upload-csv-error-icon" />
      </IconContainer>

      <Text data-aid="users-upload-csv-oversize-text">
        Your CSV exceeds the 5MB file size limit. Reduce the file size by separating your spreadsheet into separate
        files.
      </Text>

      <UploadCsvForm handleClose={handleClose} />
    </>
  );
}

function IncorrectFormatError({ handleClose }) {
  return (
    <>
      <IconContainer>
        <StyledFailedIcon data-aid="users-upload-csv-error-icon" />
      </IconContainer>

      <Text data-aid="users-upload-csv-incorrect-format-text-1">
        Your CSV file is missing required email or roles columns and could not be uploaded. Make sure your CSV file is
        formatted like{' '}
        <ExternalLink
          data-aid="csv-template-link"
          label="this template."
          to="https://cdn.document360.io/514fc4fb-ee40-466e-b10b-f9ac5d791db7/Images/Documentation/users-upload.csv"
        />
      </Text>
      <Text data-aid="users-upload-csv-incorrect-format-text-2">
        Need help?{' '}
        <ExternalLink
          data-aid="csv-template-link"
          label="Troubleshoot CSV upload"
          to="https://help.gladly.com/docs/managing-users-and-roles#troubleshooting-your-csv-upload"
        />
      </Text>

      <UploadCsvForm handleClose={handleClose} />
    </>
  );
}

const Started = React.memo((): JSX.Element => {
  return (
    <>
      <Text data-aid="users-upload-csv-started-text">Your CSV is uploading...</Text>

      <IconContainer data-aid="users-upload-csv-spinner-icon">
        <StyledSpinner />
      </IconContainer>
    </>
  );
});

function Success({ handleClose }) {
  const { usersCsvUploadState } = useResourceValues(agentUsersResource);

  return (
    <>
      <IconContainer>
        <StyledSuccessIcon data-aid="users-upload-csv-success-icon" />
      </IconContainer>

      <Text data-aid="users-upload-csv-success-text">
        {`${usersCsvUploadState.successRowCount} users were successfully added.`}
      </Text>

      <Actions>
        <Button data-aid="users-upload-csv-button-done" type={Button.Types.PRIMARY} onClick={handleClose}>
          Done
        </Button>
      </Actions>
    </>
  );
}

function ServerError({ handleClose }) {
  return (
    <>
      <IconContainer>
        <StyledFailedIcon data-aid="users-upload-csv-error-icon" />
      </IconContainer>

      <ErrorText data-aid="users-upload-csv-error-text">
        Unable to upload CSV file at this time. Come back and try again later.
      </ErrorText>

      <Actions>
        <Button data-aid="users-upload-csv-button-close" type={Button.Types.PRIMARY} onClick={handleClose}>
          Close
        </Button>
      </Actions>
    </>
  );
}

function CompletedWithErrors({ handleClose }) {
  const { usersCsvUploadState: uploadState } = useResourceValues(agentUsersResource);

  const handleDownloadErrors = () => {
    const decodedCsvErrors = atob(uploadState.errors);
    const blob = new Blob([decodedCsvErrors]);
    const timeStamp = moment().format('YYYY_MM_DD_hh_mm_ss');
    const fileName = `user_upload_errors_${timeStamp}.csv`;
    const link = window.document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <IconContainer>
        <StyledFailedIcon data-aid="users-upload-csv-error-icon" />
      </IconContainer>

      <Text data-aid="users-upload-csv-partial-text1">
        Only {uploadState.successRowCount} of {uploadState.processedRowCount} users were successfully added.
      </Text>

      <Text data-aid="users-upload-csv-partial-text2">
        Download the list of issues, fix any errors, and try uploading your CSV file again. Need help?{' '}
        <ExternalLink
          data-aid="csv-template-link"
          label="Troubleshoot CSV upload"
          to="https://help.gladly.com/docs/managing-users-and-roles#troubleshooting-your-csv-upload"
        />
      </Text>

      <Actions>
        <Button
          data-aid="users-upload-csv-button-download"
          type={Button.Types.SECONDARY}
          onClick={handleDownloadErrors}
        >
          Download issues (CSV)
        </Button>
      </Actions>

      <Actions>
        <Button data-aid="users-upload-csv-button-done" type={Button.Types.PRIMARY} onClick={handleClose}>
          Done
        </Button>
      </Actions>
    </>
  );
}

function UploadCsvForm({ handleClose }) {
  const dispatch = useDispatch();
  const ref = useRef<HTMLInputElement>(null);

  const { uploadCsvFile } = agentUsersResource.effects;
  const { usersCsvUploadStatus, usersCsvUploadState } = agentUsersResource.actions;

  const openFileDialog = () => {
    ref.current!.click();
  };

  const replaceRecording = files => {
    if (!isEmpty(files)) {
      const file = files[0];

      if (file.size <= MAX_FILE_SIZE) {
        dispatch(uploadCsvFile(file));
      } else {
        dispatch(usersCsvUploadStatus(CsvUploadStatus.ExceedsAllowedFilesizeErrror));
        dispatch(usersCsvUploadState({}));
      }
    }
  };

  return (
    <Actions>
      <Button data-aid="users-upload-csv-button-cancel" type={Button.Types.TEXT} onClick={handleClose}>
        Cancel
      </Button>
      <div>
        <StyledFileInput
          ref={ref}
          onChange={replaceRecording}
          allowedFileTypes="text/csv, .csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        />
        <Button data-aid="users-upload-csv-button-upload" onClick={openFileDialog}>
          Upload CSV
        </Button>
      </div>
    </Actions>
  );
}

const StyledEmbedModal = styled(EmbedModal)`
  .Modal-backdrop {
    background-color: transparent;
  }

  .modalCard-backdrop {
    z-index: 10;
    background-color: rgba(255, 255, 255, 0.8);
  }
`;

export const Actions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const StyledSpinner = styled(SpinnerTwo).attrs(p => ({ color: p.theme.colors.green400, size: 48, stroke: 3 }))``;

const IconContainer = styled.span`
  align-items: center;
  display: flex;
  flex: 1 1 300px;
  justify-content: center;
  width: 100%;
`;

const Text = styled.p`
  line-height: ${p => p.theme.lineHeight.base};
  min-width: 388px;
`;

const ErrorText = styled(Text)`
  color: ${p => p.theme.colors.red400};
`;

const LeftAlignedDiv = styled.div`
  text-align: left;
`;

const StyledSuccessIcon = styled(SuccessIcon)`
  fill: ${p => p.theme.colors.green400} !important;
  width: ${p => p.theme.iconSize.large};
`;

const StyledFailedIcon = styled(FailedIcon)`
  fill: ${p => p.theme.colors.red400};
  width: ${p => p.theme.iconSize.large};
`;

const StyledFileInput = styled(FileInput)`
  display: none;
`;

export default React.memo(CsvUploadModal);
